import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { ArrowDown } from 'lucide-react';
import { getSpareParts, createSparePart } from '../../../redux/features/sparePartSlice';
import { createSparePartWarehouseInput } from '../../../redux/api';
import DeviceCategorySelect from '../../inventory/DeviceCategorySelect';
import SupplierSelect from '../../inventory/SupplierSelect';
import { su } from '../../inventory/stockUi';
import NumberField, { parseNumber } from '../../inventory/NumberField';

const emptyForm = () => ({
  name: '',
  code: '',
  category: '',
  deviceCategoryId: '',
  quantity: '',
  purchasePrice: '',
  price: '',
  minQuantity: '',
  supplierId: '',
  supplierName: '',
  notes: '',
});

const SparePartInputsView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(emptyForm());

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => setFormData(emptyForm());

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const quantity = parseNumber(formData.quantity);
      const purchasePrice = parseNumber(formData.purchasePrice);
      const created = await dispatch(createSparePart({
        name: formData.name,
        code: formData.code || undefined,
        category: formData.category,
        deviceCategoryId: formData.deviceCategoryId || undefined,
        quantity: 0,
        purchasePrice,
        price: parseNumber(formData.price) || purchasePrice,
        minQuantity: parseNumber(formData.minQuantity) || undefined,
        supplierId: formData.supplierId || undefined,
        supplierName: formData.supplierName || undefined,
        description: formData.notes || undefined,
      })).unwrap();

      await createSparePartWarehouseInput({
        sparePartId: created._id,
        quantity,
        unitPrice: purchasePrice,
        documentNumber: `RD-${formData.code || Date.now()}`,
        documentDate: new Date().toISOString().split('T')[0],
        partnerName: formData.supplierName,
        notes: formData.notes,
      });

      toast.success('Rezervni deo je dodat');
      resetForm();
      dispatch(getSpareParts());
    } catch (error) {
      toast.error(error || error.message || 'Greška pri čuvanju ulaza');
    }
  };

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <h1 className={su.title}>
            <ArrowDown className={su.icon} />
            {t('spareParts.inputs.title', 'Ulazi rezervnih delova')}
          </h1>
          <p className={su.subtitle}>
            {t('spareParts.inputs.subtitle', 'Novi i polovni delovi, uključujući skinute sa aparata')}
          </p>
        </div>

        <div className={`${su.card} ${su.cardPad}`}>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className={su.grid}>
              <div>
                <label className={su.label}>Naziv *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className={su.input} />
              </div>
              <div>
                <label className={su.label}>Šifra</label>
                <input type="text" name="code" value={formData.code} onChange={handleInputChange} className={su.input} />
              </div>
              <div>
                <label className={su.label}>Kategorija</label>
                <DeviceCategorySelect
                  valueId={formData.deviceCategoryId}
                  onChange={({ id, name }) => setFormData((prev) => ({
                    ...prev,
                    deviceCategoryId: id,
                    category: name
                  }))}
                />
              </div>
              <div>
                <label className={su.label}>Količina *</label>
                <NumberField name="quantity" value={formData.quantity} onChange={handleInputChange} step="0.01" required />
              </div>
              <div>
                <label className={su.label}>Nabavna cena</label>
                <NumberField name="purchasePrice" value={formData.purchasePrice} onChange={handleInputChange} step="0.01" />
              </div>
              <div>
                <label className={su.label}>Prodajna cena</label>
                <NumberField name="price" value={formData.price} onChange={handleInputChange} step="0.01" />
              </div>
              <div>
                <label className={su.label}>Min. količina za obaveštenje</label>
                <NumberField name="minQuantity" value={formData.minQuantity} onChange={handleInputChange} min="0" />
              </div>
              <div>
                <label className={su.label}>Dobavljač</label>
                <SupplierSelect
                  valueId={formData.supplierId}
                  valueName={formData.supplierName}
                  onChange={({ id, name }) => setFormData((prev) => ({
                    ...prev,
                    supplierId: id,
                    supplierName: name
                  }))}
                />
              </div>
            </div>
            <div>
              <label className={su.label}>Napomena</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={2} className={su.textarea} />
            </div>
            <div className="flex justify-end gap-1.5">
              <button type="button" onClick={resetForm} className={su.btnGhost}>Otkaži</button>
              <button type="submit" className={su.btnPrimary}>Sačuvaj</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SparePartInputsView;
